import { HistoryOutline18Icon } from "@vygruppen/spor-icon-react";
import {
  COMPANYCODE,
  FilterType,
  ROLE,
  initStates,
} from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableFilter/constants";
import { MiniDatePicker } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableFilter/MiniDatePicker";
import { filterRoleToNorwegian } from "features/History/Filter/FilterData";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "shared/components/forms/Dropdown";
import {
  Checkbox,
  Button,
  Field,
  HStack,
  MenuItemGroup,
  MenuRadioItem,
  MenuRadioItemGroup,
  Text,
  VStack,
} from "shared/components/Spor";

const getCompanyCodeLabel = (companyCode: COMPANYCODE): string => {
  switch (companyCode) {
    case COMPANYCODE.VY:
      return "VY";
    case COMPANYCODE.VYT:
      return "VYT";
    case COMPANYCODE.ALL:
      return "VY / VYT";
  }
};

const CHECKBOXES = [
  { key: "showClosed", label: "Vis lukkede" },
  { key: "showFuture", label: "Vis frem i tid" },
  { key: "showDeleted", label: "Vis slettede" },
] as const;

type TableFilterProps = {
  setFilterParams: (update: (prev: FilterType) => FilterType) => void;
  filterParams: FilterType;
};

const TableFilter: FC<TableFilterProps> = ({
  setFilterParams,
  filterParams,
}) => {
  const { t } = useTranslation();
  const isDefault =
    filterParams.showClosed === initStates.showClosed &&
    filterParams.showFuture === initStates.showFuture &&
    filterParams.showDeleted === initStates.showDeleted &&
    filterParams.role === initStates.role &&
    filterParams.companyCode === initStates.companyCode &&
    filterParams.chosenDate.year === initStates.chosenDate.year &&
    filterParams.chosenDate.month === initStates.chosenDate.month &&
    filterParams.chosenDate.day === initStates.chosenDate.day;

  return (
    <VStack gap={2}>
      <Text fontWeight="bold" variant="md" ml={0} mr="auto">
        {t("Tjenestevarsling")}
      </Text>

      <HStack width="100%" alignItems="center" gap="12px" flexWrap="wrap">
        <Dropdown
          label={`Rolle: ${t(filterRoleToNorwegian(filterParams.role))}`}
        >
          <MenuItemGroup>
            <MenuRadioItemGroup value={filterParams.role}>
              {Object.values(ROLE).map((role) => (
                <MenuRadioItem
                  key={role}
                  value={role}
                  onClick={() => setFilterParams((prev) => ({ ...prev, role }))}
                >
                  {t(filterRoleToNorwegian(role))}
                </MenuRadioItem>
              ))}
            </MenuRadioItemGroup>
          </MenuItemGroup>
        </Dropdown>
        <Dropdown
          label={`${t("Selskap")}: ${getCompanyCodeLabel(filterParams.companyCode)}`}
        >
          <MenuItemGroup>
            <MenuRadioItemGroup value={filterParams.companyCode}>
              {Object.values(COMPANYCODE).map((code) => (
                <MenuRadioItem
                  key={code}
                  value={code}
                  onClick={() =>
                    setFilterParams((prev) => ({
                      ...prev,
                      companyCode: code,
                    }))
                  }
                >
                  {getCompanyCodeLabel(code)}
                </MenuRadioItem>
              ))}
            </MenuRadioItemGroup>
          </MenuItemGroup>
        </Dropdown>

        <HStack gap="12px">
          {CHECKBOXES.map(({ key, label }) => (
            <Checkbox
              key={key}
              checked={filterParams[key]}
              onCheckedChange={(event) => {
                setFilterParams((prev) => ({
                  ...prev,
                  [key]: !!event.checked,
                }));
              }}
            >
              {t(label)}
            </Checkbox>
          ))}
        </HStack>

        <Field maxWidth="max-content" pl="6px">
          <MiniDatePicker
            value={filterParams.chosenDate}
            onChange={(date) => {
              if (date !== null)
                setFilterParams((prev) => ({ ...prev, chosenDate: date }));
            }}
          />
        </Field>
        {!isDefault && (
          <Button
            variant="ghost"
            size="xs"
            fontWeight="normal"
            onClick={() => {
              setFilterParams(() => ({ ...initStates }));
            }}
            leftIcon={<HistoryOutline18Icon />}
          >
            {t("Tilbakestill filter")}
          </Button>
        )}
      </HStack>
    </VStack>
  );
};

export { TableFilter };
